import React, { useState } from 'react'
import SimpleHeader from '../layouts/SimpleHeader'
import SideMenu from '../layouts/SideMenu'
import MenuWrap from '../components/BurgerMenu/MenuWrap'
import ValidateEmail from '../components/EmailValidation'
import { useMutation, useQuery } from '@apollo/client'
import {
  ADD_USER_TO_ORGANIZATION_QUERY,
  GET_USERDATA_QUERY,
} from '../services/GraphQLQueries'
import LoadingPage from './LoadingPage'
import Modal from '../layouts/Modal'

const InviteUser = () => {
  const [addUserToOrganization, { loading }] = useMutation(
    ADD_USER_TO_ORGANIZATION_QUERY
  )
  const getUserDataResponse = useQuery(GET_USERDATA_QUERY, {
    fetchPolicy: 'no-cache',
  })
  const [addUserToOrgForm, setAddUserToOrgForm] = useState({
    fullName: '',
    company: '',
    email: '',
  })
  const [addUserFormErrors, setAddUserFormErrors] = useState({ errors: {} })
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleAddUserToOrgChange = (event) => {
    const { id, value } = event.target
    setAddUserToOrgForm({
      ...addUserToOrgForm,
      [id]: value,
    })
    setAddUserFormErrors((currentErrors) => ({
      errors: {
        ...currentErrors.errors,
        [id]: '',
      },
    }))
  }

  const handleAddUserToOrg = async (e) => {
    e.preventDefault()
    if (isSubmitting || loading) {
      return
    }

    let errors = {}
    let proceed = true
    if (
      addUserToOrgForm.email.length < 3 ||
      !ValidateEmail(addUserToOrgForm.email)
    ) {
      errors.email = 'Please enter a valid email address.'
      proceed = false
    }
    if (!proceed) {
      setAddUserFormErrors({ errors })
      return
    }

    setAddUserFormErrors({ errors: {} })
    setIsSubmitted(false)
    const trimmedEmail = addUserToOrgForm.email.trim()
    setSubmittedEmail(trimmedEmail)
    setIsSubmitting(true)

    try {
      const res = await addUserToOrganization({
        variables: { email: trimmedEmail },
      })

      if (res.data?.addUserToOrganization?.error) {
        setAddUserFormErrors({
          errors: {
            email: res.data.addUserToOrganization.error,
          },
        })
      } else {
        setIsSubmitted(true)
        setAddUserToOrgForm((currentForm) => ({
          ...currentForm,
          email: '',
        }))
      }
    } catch (error) {
      setAddUserFormErrors({
        errors: {
          email: 'Unable to send the invite right now. Please try again.',
        },
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (getUserDataResponse.loading) {
    return <LoadingPage />
  }

  return (
    <div id="outer-container" style={{ height: '100%' }}>
      <MenuWrap className="sm:hidden">
        <SideMenu isAuthorized={true}></SideMenu>
      </MenuWrap>
      {isSubmitted && (
        <Modal setIsModalOpen={setIsSubmitted} backGroundClass={'darkBG'}>
          <div className="mx-auto max-w-md p-6 text-center">
            <h2 className="mb-3 text-2xl font-semibold text-gray-900">
              Invite sent
            </h2>
            <p className="mb-6 text-base text-gray-700">
              Invite email sent to {submittedEmail}
            </p>
            <button
              type="button"
              className="w-full rounded-md bg-black px-5 py-3 font-semibold text-white"
              onClick={() => setIsSubmitted(false)}
            >
              OK
            </button>
          </div>
        </Modal>
      )}
      <div id="page-wrap">
        <div className="flex">
          <SideMenu isAuthorized={true} className="hidden sm:block"></SideMenu>
          <div className="w-full">
            <SimpleHeader />
            <div className="h-[calc(100vh-175px)]" aria-live="polite">
              <div className="flex justify-center items-center h-full">
                <div className="flex items-start w-full">
                  <div className="grid grid-cols-12 gap-6 w-full">
                    {/* Title */}
                    <h1 className="col-start-4 col-end-10 text-xl font-semibold text-gray-800 text-start">
                      {/* Invite User */}
                    </h1>

                    {/* Form rows */}
                    <div className="relative col-start-4 col-end-10 h-fit max-w-7xl mx-auto">
                      <div className="grid grid-cols-12 gap-y-6 gap-x-6">
                        {/* Full Name row */}
                        <div className=" w-[50rem] col-span-12 grid grid-cols-12 gap-6 items-center">
                          <div className="  col-span-5 h-14 rounded-full bg-[#F7FAF7] flex items-center px-6 shadow-sm">
                            <span className="text-lg font-unbounded text-gray-900">
                              Full Name
                            </span>
                          </div>
                          <div className="col-span-7 absolute left-[15rem] w-[30rem]">
                            <input
                              id="fullName"
                              className="h-14 rounded-full bg-[#DEE6DD] border-0 placeholder-gray-600 text-gray-800 block w-full px-8 text-lg focus:outline-none focus:ring-2 focus:ring-[#C9D7C2]"
                              placeholder="Full Name"
                              required
                              value={addUserToOrgForm.fullName}
                              onChange={handleAddUserToOrgChange}
                              disabled={isSubmitting}
                            />
                            {addUserFormErrors.errors.fullName ? (
                              <p className="mt-2 text-center font-bold text-[#f44]">
                                {addUserFormErrors.errors.fullName}
                              </p>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>

                        {/* Company row */}
                        <div className="col-span-12 grid grid-cols-12 gap-6 items-center">
                          <div className="col-span-5 h-14 rounded-full bg-[#F7FAF7] flex items-center  px-6 shadow-sm">
                            <span className="text-lg font-unbounded text-gray-900">
                              Company Name
                            </span>
                          </div>
                          <div className="col-span-7 absolute left-[15rem] w-[30rem]">
                            <input
                              id="company"
                              className="h-14 rounded-full bg-[#DEE6DD] border-0 placeholder-gray-600 text-gray-800 block w-full px-8 text-lg focus:outline-none focus:ring-2 focus:ring-[#C9D7C2]"
                              placeholder="Company Name"
                              required
                              value={addUserToOrgForm.company}
                              onChange={handleAddUserToOrgChange}
                              disabled={isSubmitting}
                            />
                            {addUserFormErrors.errors.company ? (
                              <p className="mt-2 text-center font-bold text-[#f44]">
                                {addUserFormErrors.errors.company}
                              </p>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>

                        {/* Email row */}
                        <div className="col-span-12 grid grid-cols-12 gap-6 items-center">
                          <div className="col-span-5 h-14 rounded-full bg-[#F7FAF7] flex items-center px-6 shadow-sm">
                            <span className="text-lg font-unbounded text-gray-900">
                              Email
                            </span>
                          </div>
                          <div className="col-span-7 absolute left-[15rem] w-[30rem]">
                            <input
                              type="email"
                              id="email"
                              className="h-14 rounded-full bg-[#DEE6DD] border-0 placeholder-gray-600 text-gray-800 block w-full px-8 text-lg focus:outline-none focus:ring-2 focus:ring-[#C9D7C2]"
                              placeholder="Email"
                              required
                              value={addUserToOrgForm.email}
                              onChange={handleAddUserToOrgChange}
                              disabled={isSubmitting}
                            />
                            {addUserFormErrors.errors.email ? (
                              <p className="mt-2 text-center font-bold text-[#f44]">
                                {addUserFormErrors.errors.email}
                              </p>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-start-4 col-end-10">
                      <input
                        type="button"
                        id="continue"
                        onClick={handleAddUserToOrg}
                        disabled={isSubmitting || loading}
                        className="h-12 rounded-full bg-[#CFF2C3] hover:bg-[#BFEAB3] transition font-unbounded text-gray-900 tracking-wide block w-64 mx-auto cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                        placeholder="Invite"
                        value={isSubmitting || loading ? 'SENDING...' : 'INVITE'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InviteUser
